/**
 * The inventory.
 *
 * Kate, after playing: "can you make like an inventory bar so you can look what you have
 * in your inventory?" — and separately, "when you die, you should lose all your stuff."
 *
 * So this holds what you carry, it is visible on screen, and death empties it.
 */
export type ItemId = string

export interface InventoryItem {
  id: ItemId
  label: string
  icon: string
  count: number
  /** Hunger levels restored when eaten, if it is food. */
  feeds?: number
}

export class Inventory {
  private readonly items = new Map<ItemId, InventoryItem>()
  /** Which slot is selected, for eating from the bar. */
  selected = 0

  get all(): InventoryItem[] {
    return [...this.items.values()]
  }

  get isEmpty(): boolean {
    return this.items.size === 0
  }

  count(id: ItemId): number {
    return this.items.get(id)?.count ?? 0
  }

  add(item: Omit<InventoryItem, 'count'>, howMany = 1): void {
    const existing = this.items.get(item.id)
    if (existing) existing.count += howMany
    else this.items.set(item.id, { ...item, count: howMany })
  }

  remove(id: ItemId, howMany = 1): boolean {
    const existing = this.items.get(id)
    if (!existing || existing.count < howMany) return false
    existing.count -= howMany
    if (existing.count <= 0) this.items.delete(id)
    return true
  }

  /** The item in the selected slot, if any. */
  get selectedItem(): InventoryItem | null {
    return this.all[this.selected] ?? null
  }

  select(index: number): void {
    const items = this.all
    if (items.length === 0) { this.selected = 0; return }
    this.selected = ((index % items.length) + items.length) % items.length
  }

  /** "When you die, you should lose all your stuff." */
  clear(): void {
    this.items.clear()
    this.selected = 0
  }
}

/** Meat, dropped by whatever you killed. Kate: "I still get meat from it." */
export function meatFrom(speciesName: string): Omit<InventoryItem, 'count'> {
  return {
    id: `meat-${speciesName.toLowerCase()}`,
    label: `${speciesName} meat`,
    icon: '🍖',
    /**
     * "Meat that I can eat and that replenishes 2. One hunger." She said two and then
     * one; two is taken as the figure, since it is the number she led with.
     */
    feeds: 2,
  }
}
